import { VIEW } from '../../interfaces';
import { drop, first } from 'lodash/fp';
import rootStore from '../../RootStore';
import {
    decodeCharts,
    decodeCompare,
    decodeCountryList,
    decodeDate,
    decodeDateRange,
    decodeFrequency,
    decodeHeatmapCountries,
    decodeHeatmapHighlightedCountries,
    decodeHeatmapHighlightedRisks,
    decodeHeatmapRiskType,
    decodeHeatmapScore,
    decodeRiskIds,
    decodeSingleRiskId,
    encodeCharts,
    encodeCompare,
    encodeCountryList,
    encodeDate,
    encodeDateRange,
    encodeFrequency,
    encodeHeatmapCountries,
    encodeHeatmapHighlightedCountries,
    encodeHeatmapHighlightedRisks,
    encodeHeatmapRiskType,
    encodeHeatmapScore,
    encodeRiskIds,
    encodeSingleRiskId,
    countryIdToIso,
    isoToCountryId,
} from './codecs';
import { isUrlRefDataReady } from './resolvers';

export interface ViewUrlSchema {
    isReady: () => boolean;
    ownedKeys: string[];
    encode: (view: VIEW) => Record<string, string>;
    decode: (view: VIEW, params: Record<string, string>) => void;
}

function omitEmpty(obj: Record<string, string>): Record<string, string> {
    const out: Record<string, string> = {};
    Object.keys(obj).forEach(key => {
        if (obj[key] !== '' && obj[key] != null) {
            out[key] = obj[key];
        }
    });
    return out;
}

function applyMultiCountrySelection(ids: number[]): void {
    const { countryStore } = rootStore;
    countryStore.currentStore.clean();
    ids.forEach(id => countryStore.addCountry(id));
}

export const singleCountrySchema: ViewUrlSchema = {
    isReady: isUrlRefDataReady,
    ownedKeys: ['country', 'from', 'to', 'risks', 'charts', 'frequency', 'compare'],
    encode: () => {
        const firstCountry = first(rootStore.countryStore.currentCountryList);
        const { from, to } = encodeDateRange();
        return omitEmpty({
            country: firstCountry != null ? countryIdToIso(firstCountry) ?? '' : '',
            from,
            to,
            risks: encodeRiskIds(),
            charts: encodeCharts(),
            frequency: encodeFrequency(),
            compare: encodeCompare(),
        });
    },
    decode: (_view, params) => {
        const countryId = isoToCountryId(params.country);
        if (countryId != null) {
            rootStore.countryStore.changeFirstCountry(countryId);
        }
        decodeDateRange(params.from, params.to);
        decodeRiskIds(params.risks);
        decodeCharts(params.charts);
        decodeFrequency(params.frequency);
        decodeCompare(params.compare);
    },
};

export const compareCountriesSchema: ViewUrlSchema = {
    isReady: isUrlRefDataReady,
    ownedKeys: [
        // Listed only so a legacy `view=` query param gets stripped on write;
        // it is never emitted — the active view lives in the route path.
        'view',
        'countries',
        'charts',
        'from',
        'to',
        'risks',
        'frequency',
        'compare',
        'date',
        'score',
        'risk-type',
        'highlighted_countries',
        'highlighted_risks',
    ],
    encode: view => {
        if (view === VIEW.HEATMAP) {
            return omitEmpty({
                countries: encodeHeatmapCountries(),
                highlighted_countries: encodeHeatmapHighlightedCountries(),
                highlighted_risks: encodeHeatmapHighlightedRisks(),
                date: encodeDate(rootStore.heatmapStore.startDate),
                score: encodeHeatmapScore(),
                'risk-type': encodeHeatmapRiskType(),
            });
        }

        const countries = encodeCountryList(
            rootStore.countryStore.currentCountryList
        );

        if (view === VIEW.WORLD_GRAPH) {
            const { from, to } = encodeDateRange();
            return omitEmpty({
                countries,
                charts: encodeCharts(),
                from,
                to,
                risks: encodeRiskIds(),
                frequency: encodeFrequency(),
                compare: encodeCompare(),
            });
        }
        if (view === VIEW.SORT_BY_RISK) {
            return omitEmpty({
                countries,
                risks: encodeRiskIds(),
                frequency: encodeFrequency(),
            });
        }
        // SORT_BY_DELTA uses a date range.
        const { from, to } = encodeDateRange();
        return omitEmpty({
            countries,
            from,
            to,
            risks: encodeRiskIds(),
            frequency: encodeFrequency(),
        });
    },
    decode: (view, params) => {
        if (view === VIEW.HEATMAP) {
            decodeHeatmapScore(params.score);
            decodeHeatmapRiskType(params['risk-type']);
            decodeHeatmapCountries(params.countries);
            decodeHeatmapHighlightedRisks(params.highlighted_risks);
            decodeHeatmapHighlightedCountries(params.highlighted_countries);
            const date = decodeDate(params.date);
            if (date) {
                rootStore.heatmapStore.setStartDate(date);
            }
            return;
        }

        if (params.countries != null) {
            applyMultiCountrySelection(decodeCountryList(params.countries));
        }
        decodeRiskIds(params.risks);
        if (view === VIEW.WORLD_GRAPH) {
            decodeDateRange(params.from, params.to);
            decodeCharts(params.charts);
            decodeFrequency(params.frequency);
            decodeCompare(params.compare);
        } else if (view === VIEW.SORT_BY_RISK) {
            // virtual_today handled centrally by the sync hook.
            decodeFrequency(params.frequency);
        } else if (view === VIEW.SORT_BY_DELTA) {
            decodeDateRange(params.from, params.to);
            decodeFrequency(params.frequency);
        }
    },
};

export const bilateralSchema: ViewUrlSchema = {
    isReady: isUrlRefDataReady,
    ownedKeys: [
        // Listed only so a legacy `view=` query param gets stripped on write;
        // it is never emitted — the active view lives in the route path.
        'view',
        'primary',
        'compare-countries',
        'charts',
        'from',
        'to',
        'risk-type',
        'compare',
        'frequency',
    ],
    encode: view => {
        const list = rootStore.countryStore.currentCountryList;
        const primaryId = first(list);
        const comparison = drop(1, list);
        const base = omitEmpty({
            primary: primaryId != null ? countryIdToIso(primaryId) ?? '' : '',
            'compare-countries': encodeCountryList(comparison),
        });

        if (view === VIEW.CONTINGENT_GRAPH) {
            const { from, to } = encodeDateRange();
            return omitEmpty({
                ...base,
                charts: encodeCharts(),
                from,
                to,
                'risk-type': encodeSingleRiskId(),
                compare: encodeCompare(),
            });
        }
        // SORT_BY_CONTINGENT_RISK ("Sort By Risk") uses a single date picker
        // bound to chartStore.virtualToday; the virtual_today param is handled
        // centrally by the sync hook, so don't emit a from/to range here.
        if (view === VIEW.SORT_BY_CONTINGENT_RISK) {
            return omitEmpty({
                ...base,
                'risk-type': encodeSingleRiskId(),
                frequency: encodeFrequency(),
            });
        }
        // SORT_BY_CONTINGENT_DELTA uses a date range.
        const { from, to } = encodeDateRange();
        return omitEmpty({
            ...base,
            from,
            to,
            'risk-type': encodeSingleRiskId(),
            frequency: encodeFrequency(),
        });
    },
    decode: (view, params) => {
        const primaryId = isoToCountryId(params.primary);
        if (primaryId != null) {
            rootStore.countryStore.changeFirstCountry(primaryId);
        }
        if (params['compare-countries']) {
            decodeCountryList(params['compare-countries']).forEach(id =>
                rootStore.countryStore.toggleSecondaryCountry(id)
            );
        }
        decodeSingleRiskId(params['risk-type']);
        if (view === VIEW.CONTINGENT_GRAPH) {
            decodeDateRange(params.from, params.to);
            decodeCharts(params.charts);
            decodeCompare(params.compare);
        } else if (view === VIEW.SORT_BY_CONTINGENT_RISK) {
            // virtual_today handled centrally by the sync hook.
            decodeFrequency(params.frequency);
        } else if (view === VIEW.SORT_BY_CONTINGENT_DELTA) {
            decodeDateRange(params.from, params.to);
            decodeFrequency(params.frequency);
        }
    },
};
